import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { api, type FeedCollection } from "@/api";
import { EmptyState, ErrorState } from "@/components/common/states";
import { CollectionCover } from "@/components/feed/collection-cover";
import { BagIcon, EyeOffIcon, FavoriteHeartIcon } from "@/components/icons";
import { IdeaSegmentTabs } from "@/components/idea/idea-segment-tabs";
import { Button } from "@/components/ui/button";
import { useIdeaSaved } from "@/components/idea/use-idea-saved";
import { useCategoryTree } from "@/lib/use-category-tree";
import { useFormatPrice } from "@/lib/use-format-price";
import { cn } from "@/lib/utils";
import { ideaLikeId, useDepartment, useSegment } from "@/store";
import { useIdeaHiddenInSession, useSetIdeaHidden, useVisibleIdeas } from "@/store/hidden-ideas";
import { usePreferences } from "@/store/preferences";
import { Skeleton } from "@osmix/ui/skeleton";
import { usePageMeta } from "@/lib/use-page-meta";

// Inspiration feed («Идеи OSMIX») — a Pinterest-style wall of curated product
// collections. The wall reskin (ideas_all.png) shows image-first cards: a cover
// with a save-heart, a products-count badge and a link through to the idea. The
// older captioned `CollectionCard` is kept intact below because the home page
// still renders it.

export function FeedPage() {
  const $ = _c(46);
  const {
    t
  } = useTranslation();
  usePageMeta();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["feed", "collections"],
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const feed = useQuery(t0);
  const department = useDepartment();
  const segment = useSegment();
  const setSegment = usePreferences(_temp2);
  const tree = useCategoryTree(department);
  const root = tree.segmentRoots[segment];
  const visible = useVisibleIdeas(feed.data);
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[1] !== feed || $[2] !== root || $[3] !== segment || $[4] !== setSegment || $[5] !== t || $[6] !== tree.isClothes || $[7] !== visible.ideas || $[8] !== visible.pending) {
    let t7;
    if ($[15] !== root || $[16] !== tree.isClothes) {
      t7 = c => !tree.isClothes ? c.segments.length === 0 : c.segments.length === 0 || root != null && c.segments.includes(root);
      $[15] = root;
      $[16] = tree.isClothes;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    const collections = visible.ideas.filter(t7);
    t3 = "flex flex-col";
    let t8;
    if ($[18] !== t) {
      t8 = t("feed.breadcrumbHome", {
        defaultValue: "\u0413\u043B\u0430\u0432\u043D\u0430\u044F"
      });
      $[18] = t;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== t8) {
      t9 = <Link to="/" className="rounded-sm outline-none hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">{t8}</Link>;
      $[20] = t8;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== t) {
      t10 = t("feed.breadcrumb", {
        defaultValue: "\u0418\u0434\u0435\u0438"
      });
      $[22] = t;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== t10) {
      t11 = <span aria-current="page">{t10}</span>;
      $[24] = t10;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    if ($[26] !== t11 || $[27] !== t9) {
      t4 = <nav aria-label={"\u0425\u043B\u0435\u0431\u043D\u044B\u0435 \u043A\u0440\u043E\u0448\u043A\u0438"} className="flex flex-wrap items-center gap-3 ds-t3 text-ds-primary">{t9}{t11}</nav>;
      $[26] = t11;
      $[27] = t9;
      $[28] = t4;
    } else {
      t4 = $[28];
    }
    let t12;
    if ($[29] !== t) {
      t12 = t("feed.pageTitle", {
        defaultValue: "\u0418\u0434\u0435\u0438 OSMIX"
      });
      $[29] = t;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    if ($[31] !== t12) {
      t5 = <h1 className="mt-5 ds-h2 text-ds-primary">{t12}</h1>;
      $[31] = t12;
      $[32] = t5;
    } else {
      t5 = $[32];
    }
    if ($[33] !== segment || $[34] !== setSegment || $[35] !== tree.isClothes) {
      t6 = tree.isClothes && <div className="mt-2.5"><IdeaSegmentTabs active={segment} onSelect={setSegment} /></div>;
      $[33] = segment;
      $[34] = setSegment;
      $[35] = tree.isClothes;
      $[36] = t6;
    } else {
      t6 = $[36];
    }
    t1 = "mt-4";
    t2 = feed.isPending || feed.data && visible.pending ? <IdeaWall>{["a", "b", "c", "d", "e", "f", "g", "h"].map(_temp3)}</IdeaWall> : feed.error ? <ErrorState onRetry={() => feed.refetch()} /> : collections.length > 0 ? <IdeaWall>{collections.map(_temp4)}</IdeaWall> : <EmptyState icon={Sparkles} title={t("feed.empty")} hint={t("feed.emptyHint")} />;
    $[1] = feed;
    $[2] = root;
    $[3] = segment;
    $[4] = setSegment;
    $[5] = t;
    $[6] = tree.isClothes;
    $[7] = visible.ideas;
    $[8] = visible.pending;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
  }
  let t7;
  if ($[37] !== t1 || $[38] !== t2) {
    t7 = <div className={t1}>{t2}</div>;
    $[37] = t1;
    $[38] = t2;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  let t8;
  if ($[40] !== t3 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6 || $[44] !== t7) {
    t8 = <div className={t3}>{t4}{t5}{t6}{t7}</div>;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  return t8;
}

// Стена идей — общая для ленты, «Похожих идей» и избранного. Ряды одной высоты,
// как в кадре «Все идеи» (3846:152693): колонка 270, карточка 270×362 r4, шаг
// 20; горизонтальная идея занимает две колонки той же высоты. Раньше была
// «кладка» по высоте фото, и вертикальные идеи выходили разной высоты
// (замечание Айталы 01.10). Высоту ряда считаем от ширины колонки (cqw
// контейнера), поэтому она одна и та же при любом числе колонок. Четыре колонки
// — с 1024px: в кадрах шириной 1200 их уже четыре (было с 1280, и при 1200
// стена шла в три колонки шире макетных).
function _temp4(c_0) {
  return <IdeaCard key={c_0.id} collection={c_0} />;
}
function _temp3(key, i) {
  return <Skeleton key={key} className={cn("size-full rounded-[4px]", i === 6 && "col-span-2")} />;
}
function _temp2(s) {
  return s.setSegment;
}
function _temp() {
  return api.feed.collections();
}
export function IdeaWall(t0) {
  const $ = _c(6);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("grid grid-flow-dense grid-cols-[repeat(var(--cols),minmax(0,1fr))] gap-[var(--gap)] [--cols:2] [--gap:16px] md:[--cols:3] lg:[--cols:4] lg:[--gap:20px]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gridAutoRows: "calc((100cqw - (var(--cols) - 1) * var(--gap)) / var(--cols) * 362 / 270)"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t1) {
    t3 = <div className="@container"><div className={t1} style={t2}>{children}</div></div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

// Сердечко идеи на фото («Избранное на фото»): обводка 1,5 чёрным 50 %, заливка
// белым 50 % (Айталы 02.10 утолщила обводку — было 0,5). Стена: кнопка 32×32,
// сердце 20×18 (size="card"); обложка на странице идеи: 30×30, сердце
// 18,75×16,9 (size="photo"). Сохранённая идея — сердце залито акцентом.
// Лайк — в wishlist (локально сразу, затем на шлюз). `id` — ключ лайка идеи
// `ideaLikeId(c)` (slug || id, как хранит шлюз); `altId` — id ленты, которым
// идею могли лайкнуть раньше (старые локальные лайки), см. useIdeaSaved.
export function SaveHeartButton(t0) {
  const $ = _c(17);
  const {
    id,
    altId,
    title,
    className,
    size: t1
  } = t0;
  const size = t1 === undefined ? "card" : t1;
  const {
    t
  } = useTranslation();
  const {
    saved,
    toggle
  } = useIdeaSaved(id, altId);
  let t2;
  if ($[0] !== t) {
    t2 = t("feed.save", {
      defaultValue: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0438\u0434\u0435\u044E"
    });
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== toggle) {
    t3 = e => {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    };
    $[2] = toggle;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = size === "photo" ? "size-[30px]" : "size-8";
  let t5;
  if ($[4] !== className || $[5] !== t4) {
    t5 = cn("flex items-center justify-center rounded-[4px] outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ds-focus", t4, className);
    $[4] = className;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = size === "photo" ? 30 : 32;
  let t7;
  if ($[7] !== saved || $[8] !== t6) {
    t7 = <FavoriteHeartIcon active={saved} size={t6} />;
    $[7] = saved;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== saved || $[11] !== t2 || $[12] !== t3 || $[13] !== t5 || $[14] !== t7 || $[15] !== title) {
    t8 = <button type="button" aria-pressed={saved} aria-label={t2} title={title} onClick={t3} className={t5}>{t7}</button>;
    $[10] = saved;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
    $[14] = t7;
    $[15] = title;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}

// Плитка стены (кадр «Все идеи» 3846:152693): фото во всю ячейку r4, сердечко
// 32×32 в 8px от правого верхнего угла, плашка «сумка + число товаров» в 8px
// от левого нижнего (белая 50 %, r4, отступы 2/4, зазор 3, 14/18 w400).
// Вся обложка ведёт на страницу идеи. Высоту задаёт ряд стены.
// С `onOpen` (избранное) обычный клик открывает модалку «Идею в корзину»
// вместо перехода; ссылка остаётся (Ctrl/Cmd/Shift-клик и средняя кнопка
// по-прежнему открывают /feed/:slug). Без `onOpen` поведение прежнее.
// Цена «N ₽» — только в избранном (кадр «Избранные идеи» 3871:55727); на стене
// и в «Похожих идеях» её в макете нет. По умолчанию — там, где есть `onOpen`.
// Плашка цены по кадру: 32 в высоту, r8, #2E2E2E, отступы 5/12/8/12, 14/18 w600.
// Идея, скрытая в этой сессии (A-26), показана бледной плиткой с
// перечёркнутым глазом по центру; нажатие на глаз возвращает её.
export function IdeaCard(t0) {
  const $ = _c(57);
  const {
    collection: c,
    onOpen,
    showPrice: t1
  } = t0;
  const showPrice = t1 === undefined ? Boolean(onOpen) : t1;
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  const [wide, setWide] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = e => setWide(e.currentTarget.naturalWidth > e.currentTarget.naturalHeight);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const onCoverLoad = t2;
  const hidden = useIdeaHiddenInSession(c.id);
  const setHidden = useSetIdeaHidden();
  if (hidden) {
    const t3 = wide && "col-span-2";
    let t4;
    if ($[1] !== t3) {
      t4 = cn("relative min-w-0", t3);
      $[1] = t3;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] !== c.cover) {
      t5 = <img src={c.cover} alt="" onLoad={onCoverLoad} className="size-full object-cover opacity-10" />;
      $[3] = c.cover;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] !== c || $[6] !== setHidden || $[7] !== t) {
      t6 = () => setHidden(c, false).catch(() => toast.error(t("feed.hideFailed", {
        defaultValue: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C. \u041F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u0435\u0449\u0451 \u0440\u0430\u0437"
      })));
      $[5] = c;
      $[6] = setHidden;
      $[7] = t;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== c.title || $[10] !== t) {
      t7 = t("feed.unhideNamed", {
        defaultValue: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C \u0438\u0434\u0435\u044E \xAB{{title}}\xBB",
        title: c.title
      });
      $[9] = c.title;
      $[10] = t;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== t) {
      t8 = t("feed.unhide", {
        defaultValue: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C \u0438\u0434\u0435\u044E"
      });
      $[12] = t;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <EyeOffIcon />;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    let t10;
    if ($[15] !== t6 || $[16] !== t7 || $[17] !== t8) {
      t10 = <Button variant="ghost" size="icon-lg" onClick={t6} aria-label={t7} title={t8} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-ds-ink-3">{t9}</Button>;
      $[15] = t6;
      $[16] = t7;
      $[17] = t8;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    let t11;
    if ($[19] !== t10 || $[20] !== t5) {
      t11 = <div className="relative size-full overflow-hidden rounded-[4px] bg-[#F8F8F8]">{t5}{t10}</div>;
      $[19] = t10;
      $[20] = t5;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== t11 || $[23] !== t4) {
      t12 = <article className={t4}>{t11}</article>;
      $[22] = t11;
      $[23] = t4;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    return t12;
  }
  const t3 = wide && "col-span-2";
  let t4;
  if ($[25] !== t3) {
    t4 = cn("relative min-w-0", t3);
    $[25] = t3;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  const t5 = `/feed/${c.slug}`;
  let t6;
  if ($[27] !== c || $[28] !== onOpen) {
    t6 = onOpen ? e_0 => {
      if (e_0.button !== 0 || e_0.metaKey || e_0.ctrlKey || e_0.shiftKey || e_0.altKey) {
        return;
      }
      e_0.preventDefault();
      onOpen(c);
    } : undefined;
    $[27] = c;
    $[28] = onOpen;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const t7 = onOpen ? "dialog" : undefined;
  let t8;
  if ($[30] !== c.cover || $[31] !== c.title) {
    t8 = <img src={c.cover} alt={c.title} loading="lazy" onLoad={onCoverLoad} className="size-full bg-osmix-gray-100 object-cover transition-transform duration-300 group-hover:scale-[1.03]" />;
    $[30] = c.cover;
    $[31] = c.title;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <BagIcon size={20} />;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== c.productCount) {
    t10 = <span className="absolute bottom-2 left-2 flex h-6 items-center gap-[3px] rounded-[4px] bg-white/50 px-1 py-0.5 ds-t3 text-ds-primary">{t9}{c.productCount}</span>;
    $[34] = c.productCount;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== c.totalPrice || $[37] !== fmt || $[38] !== showPrice) {
    t11 = showPrice && c.totalPrice > 0 && <span className="absolute right-2 bottom-2 flex h-8 items-center rounded-[8px] bg-ds-ink-2 px-3 pt-[5px] pb-2 ds-h5 text-white">{fmt(c.totalPrice)}</span>;
    $[36] = c.totalPrice;
    $[37] = fmt;
    $[38] = showPrice;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t5 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8) {
    t12 = <Link to={t5} onClick={t6} aria-haspopup={t7} className="group isolate block size-full overflow-hidden rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t8}{t10}{t11}</Link>;
    $[40] = t10;
    $[41] = t11;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== c) {
    t13 = ideaLikeId(c);
    $[47] = c;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== c.id || $[50] !== c.title || $[51] !== t13) {
    t14 = <SaveHeartButton id={t13} altId={c.id} title={c.title} className="absolute right-2 top-2" />;
    $[49] = c.id;
    $[50] = c.title;
    $[51] = t13;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== t12 || $[54] !== t14 || $[55] !== t4) {
    t15 = <article className={t4}>{t12}{t14}</article>;
    $[53] = t12;
    $[54] = t14;
    $[55] = t4;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  return t15;
}

// Legacy captioned card. Still used by the home page's collections rail — kept
// as-is so that surface is untouched by the «Идеи» reskin.
export function CollectionCard(t0) {
  const $ = _c(24);
  const {
    collection: c
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== c) {
    t1 = <CollectionCover collection={c} imgClassName="rounded-t-xl" />;
    $[0] = c;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `/feed/${c.slug}`;
  let t3;
  if ($[2] !== c.title) {
    t3 = <h3 className="type-h5 text-osmix-text-1 group-hover:text-osmix-tangelo">{c.title}</h3>;
    $[2] = c.title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== c.description) {
    t4 = <p className="line-clamp-2 type-body2 text-osmix-text-2">{c.description}</p>;
    $[4] = c.description;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== c.author) {
    t5 = <span className="truncate">{c.author}</span>;
    $[6] = c.author;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== c.productCount || $[9] !== t) {
    t6 = t("feed.items", {
      count: c.productCount
    });
    $[8] = c.productCount;
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <span className="shrink-0">{t6}</span>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <div className="mt-1 flex items-center justify-between type-small text-osmix-icon">{t5}{t7}</div>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t8) {
    t9 = <Link to={t2} className="group flex flex-col gap-1 rounded-b-xl p-4 outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t3}{t4}{t8}</Link>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t1 || $[22] !== t9) {
    t10 = <article className="mb-4 break-inside-avoid rounded-xl border border-osmix-gray-300 bg-card">{t1}{t9}</article>;
    $[21] = t1;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}